/**
 * The web classes of each colour tone (@tonnie/design-tokens `TONES`).
 *
 * Whole class names, so Tailwind generates every one of them: code that
 * chooses a tone at runtime (a MESH letter, a status) indexes this map and
 * never builds `bg-tone-${tone}-solid` from parts — a class assembled at
 * runtime is invisible to Tailwind and ships unstyled.
 */
import { HERO_TONE, type HeroType, type Tone } from '@tonnie/design-tokens';

export interface ToneClasses {
  /** Text or icon on the page, a card or the tone's tint. */
  text: string;
  /** Hover / emphasis text. */
  strong: string;
  /** The tone's tint, as a background. */
  subtle: string;
  /** A border on the tint. */
  line: string;
  /** A fill; pair with `onSolid` for text on it. */
  solid: string;
  onSolid: string;
  /** An SVG stroke in the fill colour (rings, arcs). */
  stroke: string;
}

export const TONE_CLASSES: Record<Tone, ToneClasses> = {
  green: {
    text: 'text-tone-green',
    strong: 'text-tone-green-strong',
    subtle: 'bg-tone-green-subtle',
    line: 'border-tone-green-line',
    solid: 'bg-tone-green-solid',
    onSolid: 'text-tone-green-on-solid',
    stroke: 'stroke-tone-green-solid',
  },
  amber: {
    text: 'text-tone-amber',
    strong: 'text-tone-amber-strong',
    subtle: 'bg-tone-amber-subtle',
    line: 'border-tone-amber-line',
    solid: 'bg-tone-amber-solid',
    onSolid: 'text-tone-amber-on-solid',
    stroke: 'stroke-tone-amber-solid',
  },
  orange: {
    text: 'text-tone-orange',
    strong: 'text-tone-orange-strong',
    subtle: 'bg-tone-orange-subtle',
    line: 'border-tone-orange-line',
    solid: 'bg-tone-orange-solid',
    onSolid: 'text-tone-orange-on-solid',
    stroke: 'stroke-tone-orange-solid',
  },
  red: {
    text: 'text-tone-red',
    strong: 'text-tone-red-strong',
    subtle: 'bg-tone-red-subtle',
    line: 'border-tone-red-line',
    solid: 'bg-tone-red-solid',
    onSolid: 'text-tone-red-on-solid',
    stroke: 'stroke-tone-red-solid',
  },
  rose: {
    text: 'text-tone-rose',
    strong: 'text-tone-rose-strong',
    subtle: 'bg-tone-rose-subtle',
    line: 'border-tone-rose-line',
    solid: 'bg-tone-rose-solid',
    onSolid: 'text-tone-rose-on-solid',
    stroke: 'stroke-tone-rose-solid',
  },
  blue: {
    text: 'text-tone-blue',
    strong: 'text-tone-blue-strong',
    subtle: 'bg-tone-blue-subtle',
    line: 'border-tone-blue-line',
    solid: 'bg-tone-blue-solid',
    onSolid: 'text-tone-blue-on-solid',
    stroke: 'stroke-tone-blue-solid',
  },
  cyan: {
    text: 'text-tone-cyan',
    strong: 'text-tone-cyan-strong',
    subtle: 'bg-tone-cyan-subtle',
    line: 'border-tone-cyan-line',
    solid: 'bg-tone-cyan-solid',
    onSolid: 'text-tone-cyan-on-solid',
    stroke: 'stroke-tone-cyan-solid',
  },
  violet: {
    text: 'text-tone-violet',
    strong: 'text-tone-violet-strong',
    subtle: 'bg-tone-violet-subtle',
    line: 'border-tone-violet-line',
    solid: 'bg-tone-violet-solid',
    onSolid: 'text-tone-violet-on-solid',
    stroke: 'stroke-tone-violet-solid',
  },
  slate: {
    text: 'text-tone-slate',
    strong: 'text-tone-slate-strong',
    subtle: 'bg-tone-slate-subtle',
    line: 'border-tone-slate-line',
    solid: 'bg-tone-slate-solid',
    onSolid: 'text-tone-slate-on-solid',
    stroke: 'stroke-tone-slate-solid',
  },
};

/**
 * The web classes of each hero: its tone's (`HERO_TONE`), and for Passport the
 * brand's — the same colours `HERO_COLORS` gives as CSS values, for code that
 * only needs a class.
 */
export const HERO_CLASSES: Record<HeroType, ToneClasses> = {
  checkup: TONE_CLASSES[HERO_TONE.checkup],
  teleportal: TONE_CLASSES[HERO_TONE.teleportal],
  qultureup: TONE_CLASSES[HERO_TONE.qultureup],
  care: TONE_CLASSES[HERO_TONE.care],
  passport: {
    text: 'text-primary-accessible',
    strong: 'text-primary-accessible',
    subtle: 'bg-primary/10',
    line: 'border-primary/20',
    solid: 'bg-primary',
    onSolid: 'text-primary-foreground',
    stroke: 'stroke-primary',
  },
};
